import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Check, Copy, ImageUp } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog } from '../components/ui/dialog';
import { Input, Select } from '../components/ui/input';
import { ProjectMark } from '../components/project/ProjectMark';
import { api, type Project, type ProjectDeletionSummary, type ProjectTreeNode } from '../lib/api';
import { useCurrentProject } from '../lib/project';
import { useProjectTree } from '../lib/project-tree';
import { cn, formatDate, timeAgo } from '../lib/utils';

/**
 * The General tab of a project's settings — its identity, its place in the hierarchy, and its
 * deletion. The home for everything the projects cards page used to own.
 *
 * Deleting and moving a project are structural acts on the hierarchy, so they belong beside the
 * project rather than on a grid of every project: the tree in the sidebar is now how you get to a
 * project, and this is what you do once you are in one.
 *
 * Renders body only — the sidebar, heading and tab strip belong to ProjectSettingsLayout, which
 * has to keep them still while you move between General, Integrations and Access.
 */
const DELETION_SUMMARY_LABELS: Record<keyof ProjectDeletionSummary, string> = {
  memoryEntries: 'Memory entries',
  stagingProposals: 'Pending review',
  fileUploads: 'File uploads',
  feedItems: 'Feed items',
  members: 'Project members',
  kbNodes: 'Knowledge base pages',
  subProjects: 'Sub-projects',
};

/**
 * A starting set for the icon picker. Curated rather than a full emoji keyboard: the point is to
 * tell a dozen clients apart at a glance, and forty distinct, mostly-object glyphs do that; anyone
 * who wants something else can type or paste it in the field beside the grid.
 */
const ICON_CHOICES = [
  '🏢', '🏗️', '🚀', '🎯', '🧭', '🧪', '🔬', '🧠', '💡', '📊',
  '📈', '📣', '🎬', '🎮', '🎨', '🎧', '🎤', '📷', '🛰️', '🛠️',
  '⚙️', '🔐', '🔑', '🌐', '🗺️', '🧩', '📦', '🗂️', '📚', '📝',
  '💼', '🤝', '🏦', '💳', '🛒', '🏥', '🎓', '🌱', '🔥', '⚡',
];

/** What the console shrinks an upload to before sending — square, and small enough that the server's cap never bites a real image. */
const ICON_PX = 128;
/** Mirrors `MAX_ICON_BYTES` on the API. Checked here so a failure is explained before the request, not after. */
const MAX_ICON_BYTES = 256 * 1024;
/** Sources larger than this are refused before decoding — a 40 MB photo would freeze the tab for nothing. */
const MAX_SOURCE_BYTES = 12 * 1024 * 1024;

/**
 * Squares and shrinks an image to `ICON_PX` — the centre crop, drawn onto a canvas, exported as PNG.
 *
 * Done in the browser deliberately: it is what makes a chosen file into an *icon* whatever its
 * shape and size, it keeps the upload to a few kilobytes so the server's cap is a backstop rather
 * than something people hit, and it means the API never needs an image library. Animated GIFs are
 * flattened to their first frame, which is the right thing for a 16px mark.
 */
async function shrinkToIcon(file: File): Promise<Blob> {
  if (file.size > MAX_SOURCE_BYTES) throw new Error('That image is too large to read — pick one under 12 MB.');
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    throw new Error('That file could not be read as an image. PNG, JPEG, GIF or WebP work.');
  }
  const side = Math.min(bitmap.width, bitmap.height);
  const canvas = document.createElement('canvas');
  canvas.width = ICON_PX;
  canvas.height = ICON_PX;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Your browser could not prepare the image.');
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(bitmap, (bitmap.width - side) / 2, (bitmap.height - side) / 2, side, side, 0, 0, ICON_PX, ICON_PX);
  bitmap.close();
  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
  if (!blob) throw new Error('Your browser could not prepare the image.');
  if (blob.size > MAX_ICON_BYTES) throw new Error(`The icon must be ${MAX_ICON_BYTES / 1024} KB or smaller.`);
  return blob;
}

/**
 * The project's mark: an uploaded image, a chosen emoji, or the default lettered tile.
 *
 * Two kinds of change with two lifecycles. The emoji is form state — `value`, saved with the rest
 * of the tab, `null` meaning "the default" — while an image upload or removal happens the moment
 * it is chosen, because a file is already a deliberate act and holding it until Save would mean
 * keeping a blob in memory to send later. The preview renders through the same component the
 * sidebar uses, so what you see here is what lands there; while an emoji is pending it takes the
 * preview over an existing image, since saving will replace that image.
 */
function ProjectIconPicker({
  project, value, onChange, onImageChanged,
}: {
  project: Project;
  value: string | null;
  onChange: (next: string | null) => void;
  /** After an upload or removal: the tree has to refetch so the sidebar shows the new mark. */
  onImageChanged: () => void;
}) {
  const fileInput = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [imageError, setImageError] = useState<string | null>(null);
  const hasImage = project.iconImageAt !== null;

  const upload = async (file: File | undefined) => {
    if (!file) return;
    setUploading(true);
    setImageError(null);
    try {
      const blob = await shrinkToIcon(file);
      await api.projects.uploadIcon(project.slug, blob);
      // The server clears the emoji when an image lands, so the form must agree or Save would
      // immediately put it back.
      onChange(null);
      onImageChanged();
    } catch (e) {
      setImageError(e instanceof Error ? e.message : String(e));
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  };

  const useDefault = async () => {
    onChange(null);
    if (!hasImage) return;
    setImageError(null);
    try {
      await api.projects.removeIcon(project.slug);
      onImageChanged();
    } catch (e) {
      setImageError(e instanceof Error ? e.message : String(e));
    }
  };

  const preview = value !== null ? { ...project, icon: value, iconImageAt: null } : { ...project, icon: null };
  const state = value !== null ? 'Emoji' : hasImage ? 'Image' : 'Default';

  return (
    <div>
      <label className="block text-xs font-medium text-ink-muted mb-1.5">Icon</label>
      <div className="flex items-start gap-4">
        <div className="flex flex-col items-center gap-1.5 flex-shrink-0">
          <div className="w-14 h-14 rounded-xl border border-border bg-surface-subtle flex items-center justify-center overflow-hidden">
            <ProjectMark project={preview} size="lg" />
          </div>
          <span className="text-[10px] text-ink-faint">{state}</span>
        </div>

        <div className="flex-1 min-w-0 space-y-2">
          <div className="flex flex-wrap gap-1">
            {ICON_CHOICES.map((emoji) => (
              <button
                key={emoji}
                type="button"
                onClick={() => onChange(emoji === value ? null : emoji)}
                aria-label={`Use ${emoji}`}
                aria-pressed={emoji === value}
                className={cn(
                  'w-8 h-8 rounded-md text-lg leading-none flex items-center justify-center transition-colors',
                  emoji === value
                    ? 'bg-orange-100 dark:bg-orange-950/50 ring-1 ring-orange-400'
                    : 'hover:bg-surface-subtle',
                )}
              >
                {emoji}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <Input
              value={value ?? ''}
              onChange={(e) => onChange(e.target.value.trim() || null)}
              placeholder="Or type any emoji"
              className="w-44 text-center"
              maxLength={32}
            />
            <input
              ref={fileInput}
              type="file"
              accept="image/png,image/jpeg,image/gif,image/webp"
              className="hidden"
              onChange={(e) => upload(e.target.files?.[0])}
            />
            <Button onClick={() => fileInput.current?.click()} disabled={uploading}>
              <ImageUp size={12} />
              {uploading ? 'Uploading…' : hasImage ? 'Replace image' : 'Upload image'}
            </Button>
            {(value !== null || hasImage) && (
              <button
                type="button"
                onClick={useDefault}
                className="text-[11px] text-ink-faint hover:text-ink transition-colors"
              >
                Use the default tile
              </button>
            )}
          </div>
          {imageError && <p className="text-[11px] text-danger-text">{imageError}</p>}
          <p className="text-[11px] text-ink-faint">
            Shown beside the name in the sidebar and wherever this project is listed. Pick an emoji, or upload a
            PNG, JPEG, GIF or WebP — it is squared and shrunk to {ICON_PX}px here, and must be under{' '}
            {MAX_ICON_BYTES / 1024} KB. An image applies straight away and replaces any emoji; an emoji is
            saved with the rest of this tab and replaces the image.
          </p>
        </div>
      </div>
    </div>
  );
}

function Section({ title, description, children }: { title: string; description?: string; children: React.ReactNode }) {
  return (
    <section className="border border-border rounded-lg bg-card p-5">
      <h2 className="text-sm font-semibold text-ink">{title}</h2>
      {description && <p className="text-xs text-ink-muted mt-1 mb-4 max-w-xl">{description}</p>}
      <div className={description ? '' : 'mt-4'}>{children}</div>
    </section>
  );
}

/**
 * What this project holds — shown, not edited.
 *
 * The description is written by the system from the project's own pages and refreshed after
 * ingestion changes the tree. There is no text box because there is no hand-written version to
 * type into: one account of a project, with provenance, rather than a human claim and a machine
 * claim that quietly disagree.
 *
 * Two controls, and they are not variations of each other. **Refresh** rewrites it now, for the two
 * moments the throttle is wrong for — just after fixing a tree, and just after filing a new project's
 * first pages, when waiting hours for placement to notice this project exists is the wrong answer.
 * **Reset** throws it away, which refresh cannot do: `summarizeProject` is *seeded* with the text
 * already there, so a description that has gone stale — a project that changed purpose — can survive
 * every refresh, while placement keeps reading it to decide what belongs here. Reset is the way out of
 * that loop, and it costs nothing permanent: the field is generated, so refresh writes it again.
 */
function ProjectDescription({ project }: { project: Project }) {
  const [description, setDescription] = useState(project.description);
  const [derivedAt, setDerivedAt] = useState(project.descriptionDerivedAt);
  const [fromPages, setFromPages] = useState(project.descriptionFromPages);
  const [busy, setBusy] = useState(false);
  /** Which control is running, so only the pressed one says so. */
  const [running, setRunning] = useState<'refresh' | 'reset' | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setDescription(project.description);
    setDerivedAt(project.descriptionDerivedAt);
    setFromPages(project.descriptionFromPages);
  }, [project.id, project.description, project.descriptionDerivedAt, project.descriptionFromPages]);

  const refresh = async () => {
    setBusy(true);
    setRunning('refresh');
    setError(null);
    try {
      const result = await api.kb.refreshDescription(project.id);
      setDescription(result.description);
      setDerivedAt(new Date().toISOString());
      setFromPages(result.pages);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
      setRunning(null);
    }
  };

  const reset = async () => {
    setBusy(true);
    setRunning('reset');
    setError(null);
    try {
      await api.kb.clearDescription(project.id);
      // The derivation metadata goes with it, or the line below would claim this was written from
      // pages minutes ago while there is nothing there to have been written.
      setDescription(null);
      setDerivedAt(null);
      setFromPages(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
      setRunning(null);
    }
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-1.5">
        <label className="block text-xs font-medium text-ink-muted">What this project holds</label>
        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-surface-muted text-ink-muted">
          written from its pages
        </span>
        <div className="ml-auto flex items-center gap-1">
          <Button size="sm" variant="ghost" onClick={refresh} disabled={busy}>
            {running === 'refresh' ? 'Rewriting…' : 'Refresh'}
          </Button>
          {/* Only when there is something to throw away. No confirmation, deliberately: the field is
              generated, Refresh is the button immediately to its left, and pressing it puts a
              description back — so this is undone by the control it sits beside. */}
          {description && (
            <Button
              size="sm"
              variant="ghost"
              onClick={reset}
              disabled={busy}
              title="Clear it, so the next rewrite starts from this project's pages instead of from the text already here."
            >
              {running === 'reset' ? 'Clearing…' : 'Reset'}
            </Button>
          )}
        </div>
      </div>

      {description ? (
        <div className="px-3 py-2 text-sm rounded-md border border-border bg-surface-subtle text-ink-secondary leading-relaxed">
          {description}
        </div>
      ) : (
        // Not an error, and worth saying exactly what to do about it: this is the cold start every
        // new project has, and the way out is filing something directly rather than waiting.
        <div className="px-3 py-2 text-xs rounded-md border border-dashed border-border text-ink-faint leading-relaxed">
          Nothing yet — a description is written once this project has a few pages. Add something to
          its knowledge base directly, and it will describe itself from then on.
        </div>
      )}

      <p className="mt-1 text-[11px] text-ink-faint">
        {derivedAt
          ? `Written from ${fromPages ?? 0} page${fromPages === 1 ? '' : 's'}, ${timeAgo(derivedAt)}. `
          : ''}
        Kept current by ingestion, and read by the router when it files content that arrived without a
        destination — a project with no description and no pages is rarely chosen.
      </p>

      {error && <p className="mt-1 text-[11px] text-danger-text">{error}</p>}
    </div>
  );
}

export default function ProjectSettings() {
  const navigate = useNavigate();
  const { project, loading } = useCurrentProject();
  const { allProjects, organization, reload } = useProjectTree();

  const [name, setName] = useState('');
  const [icon, setIcon] = useState<string | null>(null);
  const [parentId, setParentId] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!project) return;
    setName(project.name);
    setIcon(project.icon);
    setParentId(project.parentId ?? '');
  }, [project]);

  const isOrganization = project?.parentId === null;

  /**
   * Valid parents: anything that isn't this project or inside it. Moving a project beneath its
   * own descendant would detach that whole subtree from the root, so those options are removed
   * here rather than left to fail on submit.
   */
  const parentOptions = useMemo(() => {
    if (!project) return [] as ProjectTreeNode[];
    return allProjects.filter((p) => p.id !== project.id && !p.path.startsWith(`${project.path}/`));
  }, [allProjects, project]);

  // The description is not in here: it is generated, has no form state, and its Refresh acts on its
  // own rather than waiting for Save.
  const dirty = project
    ? name !== project.name || icon !== project.icon || parentId !== (project.parentId ?? '')
    : false;

  const save = async () => {
    if (!project || !dirty) return;
    setSaving(true);
    setError(null);
    try {
      await api.projects.update(project.slug, {
        ...(name !== project.name ? { name } : {}),
        // Sent as an explicit null when cleared: undefined would mean "leave it", and the server
        // reads null as "back to the default tile".
        ...(icon !== project.icon ? { icon } : {}),
        ...(parentId !== (project.parentId ?? '') ? { parentId } : {}),
      });
      reload();
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  // ─── Delete, two-step with an impact preview ────────────────────────────────
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteStep, setDeleteStep] = useState<1 | 2>(1);
  const [summary, setSummary] = useState<ProjectDeletionSummary | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const openDelete = async () => {
    if (!project) return;
    setDeleteOpen(true);
    setDeleteStep(1);
    setConfirmText('');
    setDeleteError(null);
    setPreviewLoading(true);
    try {
      const preview = await api.projects.deletionPreview(project.slug);
      setSummary(preview.summary);
    } catch (e) {
      setDeleteError(e instanceof Error ? e.message : String(e));
    } finally {
      setPreviewLoading(false);
    }
  };

  const confirmDelete = async () => {
    if (!project) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await api.projects.remove(project.slug, confirmText.trim());
      reload();
      setDeleteOpen(false);
      navigate(organization ? `/p/${organization.slug}` : '/', { replace: true });
    } catch (e) {
      setDeleteError(e instanceof Error ? e.message : String(e));
    } finally {
      setDeleting(false);
    }
  };

  if (loading || !project) return <p className="text-xs text-ink-faint">Loading…</p>;

  const hasSubProjects = (summary?.subProjects ?? 0) > 0;

  return (
    <>
      <div className="space-y-4">
        <Section
          title="Identity"
          description={
            isOrganization
              ? 'This is the organization — the root of the hierarchy. It can be renamed, but it cannot be moved or deleted.'
              : 'The name and icon shown in the sidebar, what this project is for, and where it sits in the hierarchy.'
          }
        >
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-ink-muted mb-1.5">Name</label>
              <Input value={name} onChange={(e) => setName(e.target.value)} />
            </div>

            <ProjectIconPicker project={project} value={icon} onChange={setIcon} onImageChanged={reload} />

            <ProjectDescription project={project} />

            <div>
              <label className="block text-xs font-medium text-ink-muted mb-1.5">Slug</label>
              <Input value={project.slug} disabled className="font-mono text-xs" />
              <p className="mt-1 text-[11px] text-ink-faint">
                The slug is part of every URL to this project and cannot be changed here.
              </p>
            </div>

            {!isOrganization && (
              <div>
                <label className="block text-xs font-medium text-ink-muted mb-1.5">Parent</label>
                <Select value={parentId} onChange={(e) => setParentId(e.target.value)}>
                  {parentOptions.map((p) => (
                    <option key={p.id} value={p.id}>
                      {' '.repeat(p.depth * 2)}
                      {p.name}
                    </option>
                  ))}
                </Select>
                <p className="mt-1 text-[11px] text-ink-faint">
                  Moving a project takes its sub-projects with it. Its knowledge base moves too, so what it can
                  read changes to match its new branch.
                </p>
              </div>
            )}

            <div className="flex items-center gap-2 pt-1">
              <Button variant="primary" onClick={save} disabled={!dirty || saving}>
                {saving ? 'Saving…' : 'Save changes'}
              </Button>
              {saved && (
                <span className="text-[11px] text-green-600 dark:text-green-400 inline-flex items-center gap-1">
                  <Check size={11} /> Saved
                </span>
              )}
              {error && <span className="text-[11px] text-danger-text">{error}</span>}
            </div>
          </div>
        </Section>

        <Section title="Reference" description="Identifiers other tools need — the MCP tools all take a project id.">
          <div className="flex items-center gap-2">
            <Input value={project.id} readOnly className="font-mono text-xs" />
            <Button
              onClick={() => {
                navigator.clipboard.writeText(project.id);
                setCopied(true);
                setTimeout(() => setCopied(false), 1500);
              }}
            >
              {copied ? <Check size={13} /> : <Copy size={13} />}
            </Button>
          </div>
          <p className="mt-2 text-[11px] text-ink-faint">Created {formatDate(project.createdAt)}</p>
        </Section>

        {!isOrganization && (
          <Section
            title="Delete this project"
            description="Permanently removes this project and everything in it, including its knowledge base. This cannot be undone."
          >
            <Button variant="danger" onClick={openDelete}>
              Delete project
            </Button>
          </Section>
        )}
      </div>

      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} title={`Delete "${project.name}"?`}>
        {deleteStep === 1 ? (
          <div className="space-y-4">
            {deleteError && (
              <p className="text-xs text-danger-text bg-danger-bg border border-danger-ring rounded px-3 py-2">
                {deleteError}
              </p>
            )}

            {previewLoading ? (
              <p className="text-sm text-ink-faint">Loading impact…</p>
            ) : summary ? (
              <>
                {/* Surfaced before the confirm step rather than as a failure after it: a project
                    with children cannot be deleted at all, and finding that out at the end of a
                    two-step destructive flow is the wrong place to learn it. */}
                {hasSubProjects && (
                  <p className="text-xs text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-900 rounded px-3 py-2 flex gap-2">
                    <AlertTriangle size={13} className="flex-shrink-0 mt-0.5" />
                    <span>
                      This project has {summary.subProjects} sub-project
                      {summary.subProjects === 1 ? '' : 's'}. Move or delete{' '}
                      {summary.subProjects === 1 ? 'it' : 'them'} first — deleting a parent would take every
                      knowledge base beneath it too.
                    </span>
                  </p>
                )}
                <p className="text-sm text-ink-muted">
                  Deleting this project permanently removes the following. This action cannot be undone.
                </p>
                <ul className="space-y-1.5 bg-danger-bg border border-danger-ring rounded-lg px-4 py-3">
                  {(Object.keys(DELETION_SUMMARY_LABELS) as Array<keyof ProjectDeletionSummary>).map((key) => (
                    <li key={key} className="flex items-center justify-between text-sm">
                      <span className="text-ink-muted">{DELETION_SUMMARY_LABELS[key]}</span>
                      <span className={cn('font-semibold', summary[key] > 0 ? 'text-danger-text' : 'text-ink-faint')}>
                        {summary[key]}
                      </span>
                    </li>
                  ))}
                </ul>
              </>
            ) : null}

            <div className="flex justify-end gap-2 pt-2">
              <Button onClick={() => setDeleteOpen(false)}>Cancel</Button>
              <Button
                variant="danger"
                onClick={() => setDeleteStep(2)}
                disabled={previewLoading || !summary || hasSubProjects}
              >
                Continue
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {deleteError && (
              <p className="text-xs text-danger-text bg-danger-bg border border-danger-ring rounded px-3 py-2">
                {deleteError}
              </p>
            )}
            <p className="text-sm text-ink-muted">
              Type <span className="font-mono font-semibold text-ink">delete</span> to confirm you want to permanently
              delete <span className="font-semibold text-ink">{project.name}</span> and all its data.
            </p>
            <Input
              placeholder="delete"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  confirmDelete();
                }
              }}
              autoFocus
            />
            <div className="flex justify-end gap-2 pt-2">
              <Button onClick={() => setDeleteStep(1)} disabled={deleting}>
                Back
              </Button>
              <Button
                variant="danger"
                onClick={confirmDelete}
                disabled={deleting || confirmText.trim().toLowerCase() !== 'delete'}
              >
                {deleting ? 'Deleting…' : 'Delete project'}
              </Button>
            </div>
          </div>
        )}
      </Dialog>
    </>
  );
}
